import React, { useEffect, useState } from 'react';
import {
  X,
  Ship,
  Hash,
  Boxes,
  LayoutGrid,
  Users,
  Container as ContainerIcon,
  Building2,
  PackageCheck,
  BellRing,
  Check,
  Copy,
  FileText,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import type { BillRow, BillModalData } from '../interfaces/bill-row.interface';
import { fmtDate } from '@/features/dashboard/data/claudeMockData';
import { useDirection } from '@/contexts/DirectionContext';
import {
  ALL_CONTAINER_STATUSES,
  containerStatusLabel,
} from '@/features/containers/constants/statuses';
import {
  formatEtaHeading,
  formatEtaSubtext,
} from '@/features/containers/constants/directionLabels';
import { useBillModal } from '../hooks/useBillModal';
import { ClientNoteEditor } from '@/components/claude/ClientNoteEditor';
import { useSaveClientNote } from '@/hooks/useClientNote';
import { OvGroup, OvLongRow, OvRow, stripPortCode } from '@/features/containers/components/OverviewGroups';
import { BillDocumentsPanel } from '@/features/containers/components/BillDocumentsPanel';

type TabId = 'overview' | 'parties' | 'containers' | 'docs';

interface BillPreviewModalProps {
  isOpen: boolean;
  bill: BillRow | null;
  onClose: () => void;
}

/**
 * Modal claude-design pentru detalii conosament.
 *
 * Conosamentul oficial (PDF-ul standard Yang Ming) NU este reprodus aici —
 * acela se generează exclusiv în sistemul Yang Ming. Modal-ul afișează doar
 * câmpurile populate în baza noastră pentru bookingurile de import.
 */
export const BillPreviewModal: React.FC<BillPreviewModalProps> = ({
  isOpen,
  bill,
  onClose,
}) => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';

  const { data, isLoading } = useBillModal(isOpen && bill ? bill.id : null);
  const [tab, setTab] = useState<TabId>('overview');
  const { direction } = useDirection();
  // Avizarea de sosire, cererea de eliberare și LOI-ul sunt documente de import (consignee la Constanța).
  const showDocsTab = direction !== 'export';

  useEffect(() => {
    if (isOpen) setTab('overview');
  }, [isOpen, bill?.id]);

  useEffect(() => {
    if (!showDocsTab && tab === 'docs') setTab('overview');
  }, [showDocsTab, tab]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen || !bill) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="modal-title">
              <span className="mono">{bill.code || '—'}</span>
            </div>
            <div className="modal-sub">
              {bill.bookingNumber ? (
                <>
                  Booking <span className="mono">{bill.bookingNumber}</span>
                  {bill.companyName ? ` · ${bill.companyName}` : ''}
                </>
              ) : (
                bill.companyName
              )}
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label={isRo ? 'Închide' : 'Close'}
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-tabs" role="tablist">
          {(
            [
              { id: 'overview', ro: 'Privire generală', en: 'Overview', icon: LayoutGrid, count: null },
              { id: 'parties', ro: 'Părți', en: 'Parties', icon: Users, count: null },
              {
                id: 'containers',
                ro: 'Containere',
                en: 'Containers',
                icon: ContainerIcon,
                count: data ? data.containers.length : bill.containerCount,
              },
              ...(showDocsTab ? [{ id: 'docs', ro: 'Documente', en: 'Documents', icon: FileText, count: null }] : []),
            ] as Array<{ id: TabId; ro: string; en: string; icon: React.ElementType; count: number | null }>
          ).map((tb) => {
            const Icon = tb.icon;
            return (
              <button
                key={tb.id}
                type="button"
                role="tab"
                aria-selected={tab === tb.id}
                className={'modal-tab' + (tab === tb.id ? ' active' : '')}
                onClick={() => setTab(tb.id)}
              >
                <Icon size={14} strokeWidth={2} />
                {isRo ? tb.ro : tb.en}
                {tb.count != null && tb.count > 0 && <span className="modal-tab-count">{tb.count}</span>}
              </button>
            );
          })}
        </div>

        <div className="modal-body">
          {isLoading || !data ? (
            <div
              className="empty"
              style={{
                minHeight: 240,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div className="fw-600 fs-14">{isRo ? 'Se încarcă…' : 'Loading…'}</div>
            </div>
          ) : (
            <>
              {tab === 'overview' && <OverviewTab data={data} lang={lang} />}
              {tab === 'parties' && <PartiesTab data={data} lang={lang} />}
              {tab === 'containers' && <ContainersTab data={data} lang={lang} />}
              {tab === 'docs' && (
                <BillDocumentsPanel billId={data.id} releaseType={data.releaseType ?? null} status={data.status} lang={lang} />
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-sm" onClick={onClose}>
            {isRo ? 'Închide' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Privire generală
// ============================================================

/** Progresul pe drum, estimat din ETD → ETA (B/L-ul nu are progres calculat în backend). */
function estimateProgress(data: BillModalData): number {
  if (['discharged', 'in_delivery', 'completed'].includes(data.status)) return 1;
  if (!data.etd || !data.eta) return 0.5;
  const from = new Date(data.etd).getTime();
  const to = new Date(data.eta).getTime();
  if (!(to > from)) return 0.5;
  return Math.min(0.97, Math.max(0.03, (Date.now() - from) / (to - from)));
}

const OverviewTab: React.FC<{ data: BillModalData; lang: 'ro' | 'en' }> = ({ data, lang }) => {
  const isRo = lang === 'ro';
  const { direction } = useDirection();
  const isExport = direction === 'export';
  const st = ALL_CONTAINER_STATUSES[data.status];
  const { saveBill } = useSaveClientNote();
  const progress = estimateProgress(data);

  return (
    <div>
      <div className="ov-surface bl-route-card">
        <div className="row gap-3" style={{ flexWrap: 'wrap', marginBottom: 14 }}>
          {!isExport && data.shippingType && <span className="badge badge-neutral">{data.shippingType}</span>}
          {data.isWayBill && <span className="badge badge-info">Sea Waybill</span>}
          {data.isFinalized && <span className="badge badge-success">{isRo ? 'Finalizat' : 'Finalized'}</span>}
          {st && (
            <span className={'badge ' + st.badge}>
              <span className="dot" />
              {containerStatusLabel(data.status, lang)}
            </span>
          )}
        </div>

        <div className="ctn-route-row" style={{ alignItems: 'center', marginTop: 8 }}>
          <div className="ctn-route-pol">
            <div className="muted fs-11">POL</div>
            <div className="fw-600 fs-18" style={{ letterSpacing: '-0.015em' }}>
              {data.polName || data.pol || '—'}
            </div>
            <div className="muted fs-12 mono">{data.pol || ''}</div>
            <div className="muted fs-12 mt-2">ETD {fmtDate(data.etd, lang)}</div>
          </div>

          <div className="ctn-route-track" style={{ minWidth: 0 }}>
            <div className="bl-track">
              <div className="bl-track-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
              {progress > 0 && progress < 1 && (
                <span className="bl-track-ship" style={{ left: `${progress * 100}%` }}>
                  <Ship size={12} strokeWidth={2} />
                </span>
              )}
            </div>
            <div className="muted fs-11" style={{ textAlign: 'center', marginTop: 10 }}>
              {data.vesselName || '—'}
              {data.vesselCode ? ` · ${data.vesselCode}` : ''}
            </div>
          </div>

          <div className="ctn-route-pod">
            <div className="muted fs-11">POD</div>
            <div className="fw-600 fs-18" style={{ letterSpacing: '-0.015em' }}>
              {data.podName || data.pod || '—'}
            </div>
            <div className="muted fs-12 mono">{data.pod || ''}</div>
            <div className="muted fs-12 mt-2">
              {isExport ? formatEtaHeading(direction, lang, data) : 'ETA'} {fmtDate(data.eta, lang)}
              {data.eta && isExport && formatEtaSubtext(direction, lang, data) ? (
                <div className="muted fs-11 mt-1">{formatEtaSubtext(direction, lang, data)}</div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="ov-groups">
        <OvGroup icon={Hash} title={isRo ? 'Referințe' : 'References'}>
          <OvRow label="B/L" value={data.code} mono copy />
          {(!isExport || data.bookingNumber) && <OvRow label="Booking" value={data.bookingNumber} mono copy />}
          {(!isExport || data.tradeId) && <OvRow label="Trade ID" value={data.tradeId} mono copy />}
          {(!isExport || data.gcCode) && <OvRow label={isRo ? 'Cod GC' : 'GC code'} value={data.gcCode} mono copy />}
        </OvGroup>

        <OvGroup icon={Ship} title="Transport">
          {!isExport && <OvRow label={isRo ? 'Navă' : 'Vessel'} value={data.vesselName} sub={data.vesselCode} />}
          {isExport && data.feederName && <OvRow label="Feeder" value={data.feederName} sub={data.feederCode} />}
          {isExport && data.motherName && (
            <OvRow label={isRo ? 'Navă mamă' : 'Mother vessel'} value={data.motherName} sub={data.motherCode} />
          )}
          {isExport && data.lastFeederName && (
            <OvRow label={isRo ? 'Ultimul feeder' : 'Last feeder'} value={data.lastFeederName} sub={data.lastFeederCode} />
          )}
          {isExport && data.tsPort1 && (
            <OvRow
              label={data.tsPort2 ? 'Transshipment 1' : 'Transshipment'}
              value={stripPortCode(data.tsPort1Name) || data.tsPort1}
              sub={data.tsPort1Name ? data.tsPort1 : null}
            />
          )}
          {isExport && data.tsPort2 && (
            <OvRow label="Transshipment 2" value={stripPortCode(data.tsPort2Name) || data.tsPort2} sub={data.tsPort2Name ? data.tsPort2 : null} />
          )}
          {(!isExport || data.onBoardDate) && (
            <OvRow label="On Board" value={data.onBoardDate ? fmtDate(data.onBoardDate, lang) : null} />
          )}
          {(!isExport || data.issued) && (
            <OvRow label={isRo ? 'Emis' : 'Issued'} value={data.issued ? fmtDate(data.issued, lang) : null} />
          )}
          {(!isExport || data.dischargeDate) && (
            <OvRow
              label={isRo ? 'Descărcat la' : 'Discharged on'}
              value={data.dischargeDate ? fmtDate(data.dischargeDate, lang) : null}
            />
          )}
          {(!isExport || data.terminal) && <OvRow label="Terminal" value={data.terminal} />}
        </OvGroup>

        <OvGroup icon={Boxes} title={isRo ? 'Marfă' : 'Cargo'}>
          {(!isExport || data.commodity) && (
            <OvLongRow label={isRo ? 'Descriere (orig.)' : 'Description (orig.)'} value={data.commodity} isRo={isRo} />
          )}
          {(!isExport || data.translatedCommodity) && (
            <OvLongRow label={isRo ? 'Descriere (RO)' : 'Description (RO)'} value={data.translatedCommodity} isRo={isRo} />
          )}
          {(!isExport || data.hsCode) && <OvRow label={isRo ? 'Cod HS' : 'HS code'} value={data.hsCode} mono copy />}
          {(!isExport || data.teus != null) && (
            <OvRow label="TEU" value={data.teus != null ? String(data.teus) : null} mono />
          )}
          <OvRow label={isRo ? 'Containere' : 'Containers'} value={String(data.containers.length)} mono />
        </OvGroup>
      </div>

      <div style={{ marginTop: 18 }}>
        <ClientNoteEditor
          value={data.clientNote}
          onSave={(next) => saveBill(data.id, next).then(() => undefined)}
          variant="block"
          lang={lang}
        />
      </div>
    </div>
  );
};

// ============================================================
// Părți
// ============================================================

const PartyCard: React.FC<{
  role: string;
  icon: React.ElementType;
  name: string | null;
  address: string | null;
  isRo: boolean;
}> = ({ role, icon: Icon, name, address, isRo }) => {
  const [copied, setCopied] = useState(false);
  const text = [name, address].filter(Boolean).join('\n');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      // clipboard indisponibil — nu blocăm nimic
    }
  };
  return (
    <section className="pt-card">
      <header className="pt-head">
        <span className="pt-icon">
          <Icon size={14} strokeWidth={2.2} />
        </span>
        <span className="pt-role">{role}</span>
        {text && (
          <button
            type="button"
            className={'ov-copy pt-copy' + (copied ? ' copied' : '')}
            onClick={copy}
            aria-label={copied ? (isRo ? 'Copiat' : 'Copied') : `${isRo ? 'Copiază' : 'Copy'} ${role}`}
            title={copied ? (isRo ? 'Copiat' : 'Copied') : isRo ? 'Copiază numele și adresa' : 'Copy name and address'}
          >
            {copied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={2} />}
          </button>
        )}
      </header>
      <div className="pt-name">{name || '—'}</div>
      {address && <div className="pt-address">{address}</div>}
    </section>
  );
};

const PartiesTab: React.FC<{ data: BillModalData; lang: 'ro' | 'en' }> = ({ data, lang }) => {
  const isRo = lang === 'ro';
  const { direction } = useDirection();
  const isExport = direction === 'export';
  const parties = [
    { key: 'shipper', role: isRo ? 'Expeditor (shipper)' : 'Shipper', icon: Building2, name: data.shipperName, address: data.shipperAddress },
    { key: 'consignee', role: isRo ? 'Destinatar (consignee)' : 'Consignee', icon: PackageCheck, name: data.consigneeName, address: data.consigneeAddress },
    { key: 'notify', role: 'Notify', icon: BellRing, name: data.notifyName, address: data.notifyAddress },
  ].filter((p) => !isExport || p.name || p.address); // pe export ascundem părțile necompletate

  if (parties.length === 0) {
    return (
      <div className="empty">
        <div className="icon">
          <Users size={22} strokeWidth={2} />
        </div>
        <div className="fw-600">{isRo ? 'Nicio parte completată' : 'No parties filled in'}</div>
      </div>
    );
  }
  return (
    <div className="pt-grid">
      {parties.map((p) => (
        <PartyCard key={p.key} role={p.role} icon={p.icon} name={p.name} address={p.address} isRo={isRo} />
      ))}
    </div>
  );
};

// ============================================================
// Containere + linii de marfă
// ============================================================

const ContainersTab: React.FC<{ data: BillModalData; lang: 'ro' | 'en' }> = ({ data, lang }) => {
  const isRo = lang === 'ro';
  const { saveContainer } = useSaveClientNote();
  const fmtNumber = (n: number | null, suffix = '') =>
    n == null ? '—' : `${Number(n).toLocaleString('ro-RO')}${suffix}`;
  const cargoRows = data.cargos.some((c) => c.hsCode || c.packageNumber || c.packageType || c.weight || c.volume);

  if (data.containers.length === 0 && !cargoRows) {
    return (
      <div className="empty">
        <div className="icon">
          <ContainerIcon size={22} strokeWidth={2} />
        </div>
        <div className="fw-600">{isRo ? 'Niciun container pe acest B/L' : 'No containers on this B/L'}</div>
      </div>
    );
  }

  return (
    <div>
      {data.containers.length > 0 && (
        <div className="bc-list">
          {data.containers.map((c) => (
            <div key={c.id} className="bc-row">
              <div className="bc-main">
                <div className="bc-no mono">{c.containerNumber || '—'}</div>
                <div className="bc-meta">
                  {c.containerType && <span className="mc-type">{c.containerType}</span>}
                  {c.sealNumber && (
                    <span>
                      {isRo ? 'Sigiliu' : 'Seal'} <span className="mono">{c.sealNumber}</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="bc-stats">
                <div className="bc-stat">
                  <span className="k">{isRo ? 'Greutate' : 'Weight'}</span>
                  <span className="v mono">{c.grossWeight != null && c.grossWeight > 0 ? fmtNumber(c.grossWeight, ' kg') : '—'}</span>
                </div>
                <div className="bc-stat">
                  <span className="k">{isRo ? 'Colete' : 'Packages'}</span>
                  <span className="v mono">{c.packageNumber != null && c.packageNumber > 0 ? c.packageNumber : '—'}</span>
                </div>
              </div>
              <div className="bc-note">
                <ClientNoteEditor
                  value={c.clientNote}
                  onSave={(next) => saveContainer(c.id, next).then(() => undefined)}
                  variant="cell"
                  lang={lang}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {cargoRows && (
        <div style={{ marginTop: 20 }}>
          <div className="bc-section">
            {isRo ? 'Linii marfă' : 'Cargo lines'}
            <span className="muted"> · {data.cargos.length}</span>
          </div>
          <div className="bc-cargo">
            <table className="data compact" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>{isRo ? 'Cod HS' : 'HS code'}</th>
                  <th>{isRo ? 'Colete' : 'Packages'}</th>
                  <th>{isRo ? 'Tip' : 'Type'}</th>
                  <th style={{ textAlign: 'right' }}>{isRo ? 'Greutate' : 'Weight'}</th>
                  <th style={{ textAlign: 'right' }}>{isRo ? 'Volum' : 'Volume'}</th>
                </tr>
              </thead>
              <tbody>
                {data.cargos.map((c) => (
                  <tr key={c.id}>
                    <td className="mono fs-12">{c.hsCode || '—'}</td>
                    <td className="mono fs-12">{c.packageNumber ?? '—'}</td>
                    <td className="fs-12">{c.packageType || '—'}</td>
                    <td className="mono fs-12" style={{ textAlign: 'right' }}>
                      {c.weight != null ? fmtNumber(c.weight, ' kg') : '—'}
                    </td>
                    <td className="mono fs-12" style={{ textAlign: 'right' }}>
                      {c.volume != null ? fmtNumber(c.volume, ' m³') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
